import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckSquare,
  Edit,
  Film,
  Loader2,
  Plus,
  Square,
  Trash2,
} from 'lucide-react';
import { api } from '../../services/api';

const AdminCollectionManager = () => {
  const { collectionName } = useParams();
  const decodedName = decodeURIComponent(collectionName);

  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    loadScenes();
  }, [collectionName]);

  const loadScenes = async () => {
    try {
      setLoading(true);
      const data = await api.getPacks({ collection: decodedName });
      const list = Array.isArray(data) ? data : [];
      setScenes(list.filter((scene) => scene.collection === decodedName));
      setSelectedIds([]);
    } catch (err) {
      console.error('Erro ao carregar cenas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sceneId) => {
    try {
      setDeleting(sceneId);
      await api.deletePack(sceneId);
      setScenes((prev) => prev.filter((scene) => scene.id !== sceneId));
      setSelectedIds((prev) => prev.filter((id) => id !== sceneId));
      localStorage.removeItem('packs_cache_v1');
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Erro ao deletar:', err);
      alert('Erro ao deletar cena');
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    try {
      setBulkDeleting(true);
      const result = await api.bulkDeletePacks(selectedIds);
      const deletedIds = Array.isArray(result?.deleted)
        ? result.deleted.map((item) => item.id)
        : [];

      setScenes((prev) => prev.filter((scene) => !deletedIds.includes(scene.id)));
      setSelectedIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
      localStorage.removeItem('packs_cache_v1');
      setShowDeleteConfirm(null);

      const failedCount = result?.summary?.failed || 0;
      if (failedCount > 0) {
        alert(`${deletedIds.length} cenas deletadas. ${failedCount} falharam e permaneceram na lista.`);
      }
    } catch (err) {
      console.error('Erro ao deletar em lote:', err);
      alert('Erro ao deletar cenas selecionadas');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelection = (sceneId) => {
    setSelectedIds((prev) => (
      prev.includes(sceneId)
        ? prev.filter((id) => id !== sceneId)
        : [...prev, sceneId]
    ));
  };

  const allSelected = scenes.length > 0 && selectedIds.length === scenes.length;

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : scenes.map((scene) => scene.id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FFCC00]/10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#FFCC00]" />
          </div>
          <p className="text-white/40 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[30%] w-[500px] h-[500px] bg-[#FFCC00]/[0.02] rounded-full blur-[100px]" />
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/[0.02] border-b border-white/[0.05]">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="w-10 h-10 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] flex items-center justify-center transition-all"
              >
                <ArrowLeft className="w-5 h-5 text-white/50" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFCC00]/20 to-[#FFCC00]/5 border border-[#FFCC00]/20 flex items-center justify-center">
                  <Film className="w-5 h-5 text-[#FFCC00]" />
                </div>
                <div>
                  <h1 className="text-white font-medium tracking-tight">{decodedName}</h1>
                  <p className="text-white/30 text-xs">
                    {scenes.length} cenas{selectedIds.length > 0 ? ` | ${selectedIds.length} selecionadas` : ''}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {scenes.length > 0 && (
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 text-sm transition-all"
                >
                  {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  <span>{allSelected ? 'Limpar selecao' : 'Selecionar todas'}</span>
                </button>
              )}

              <Link
                to="/admin/scenes/new"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/25 text-[#FFCC00] text-sm transition-all hover:shadow-lg hover:shadow-[#FFCC00]/10"
              >
                <Plus className="w-4 h-4" />
                <span>Nova Cena</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8">
        {selectedIds.length > 0 && (
          <div className="mb-6 backdrop-blur-xl bg-red-500/10 rounded-2xl border border-red-500/20 p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-red-300 font-medium text-sm">{selectedIds.length} cenas selecionadas</p>
              <p className="text-white/50 text-xs">Os arquivos serao removidos do storage e do banco.</p>
            </div>
            <button
              onClick={() => setShowDeleteConfirm({ type: 'bulk', count: selectedIds.length })}
              className="px-4 py-2 rounded-xl bg-red-500/15 hover:bg-red-500/20 border border-red-500/25 text-red-300 text-sm font-medium transition-all"
            >
              Excluir selecionadas
            </button>
          </div>
        )}

        {scenes.length === 0 ? (
          <div className="backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] p-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.05] flex items-center justify-center mx-auto mb-4">
              <Film className="w-7 h-7 text-white/20" />
            </div>
            <p className="text-white/40">Nenhuma cena nesta colecao</p>
            <Link
              to="/admin/scenes/new"
              className="inline-flex items-center gap-2 px-4 py-2 mt-4 rounded-xl bg-[#FFCC00]/10 hover:bg-[#FFCC00]/15 border border-[#FFCC00]/25 text-[#FFCC00] text-sm transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar</span>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {scenes.map((scene) => (
              <div
                key={scene.id}
                className="group relative backdrop-blur-xl bg-white/[0.02] rounded-2xl border border-white/[0.05] overflow-hidden hover:bg-white/[0.04] hover:border-[#FFCC00]/20 transition-all duration-500"
              >
                <button
                  type="button"
                  onClick={() => toggleSelection(scene.id)}
                  className="absolute top-3 left-3 z-20 w-8 h-8 rounded-xl bg-black/60 hover:bg-black/75 border border-white/10 flex items-center justify-center text-white/80 transition-all"
                >
                  {selectedIds.includes(scene.id) ? <CheckSquare className="w-4 h-4 text-[#FFCC00]" /> : <Square className="w-4 h-4" />}
                </button>

                <div className="aspect-[9/16] relative bg-black/50">
                  {scene.thumbnail_url ? (
                    <img
                      src={scene.thumbnail_url}
                      alt={scene.title}
                      className="w-full h-full object-cover"
                    />
                  ) : scene.video_url ? (
                    <video
                      src={scene.video_url}
                      className="w-full h-full object-cover"
                      muted
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-8 h-8 text-white/20" />
                    </div>
                  )}

                  {scene.duration && (
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs">
                      {scene.duration.toFixed(1)}s
                    </div>
                  )}

                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Link
                      to={`/admin/scene/${scene.id}/edit`}
                      className="p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
                    >
                      <Edit className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => setShowDeleteConfirm(scene)}
                      className="p-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-3">
                  <p className="text-white text-sm font-medium truncate">{scene.title}</p>
                  {scene.is_4k && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-[#FFCC00]/10 text-[#FFCC00] text-xs rounded-full border border-[#FFCC00]/20">
                      4K
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setShowDeleteConfirm(null)} />

          <div className="relative backdrop-blur-2xl bg-[#0a0a0a]/90 rounded-3xl border border-white/[0.08] p-8 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-lg">
                  {showDeleteConfirm.type === 'bulk' ? 'Deletar Cenas' : 'Deletar Cena'}
                </h3>
                <p className="text-white/40 text-sm">
                  {showDeleteConfirm.type === 'bulk'
                    ? `${showDeleteConfirm.count} cenas selecionadas`
                    : showDeleteConfirm.title}
                </p>
              </div>
            </div>

            <p className="text-white/60 text-sm mb-6">
              {showDeleteConfirm.type === 'bulk'
                ? 'Esta acao nao pode ser desfeita. Todos os videos selecionados serao removidos permanentemente.'
                : 'Esta acao nao pode ser desfeita. O video sera removido permanentemente.'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] text-white/70 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={() => (showDeleteConfirm.type === 'bulk' ? handleBulkDelete() : handleDelete(showDeleteConfirm.id))}
                disabled={bulkDeleting || deleting === showDeleteConfirm.id}
                className="flex-1 px-4 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/15 border border-red-500/25 text-red-400 font-medium transition-all disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {(bulkDeleting || deleting === showDeleteConfirm.id)
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Trash2 className="w-4 h-4" />}
                <span>{showDeleteConfirm.type === 'bulk' ? 'Deletar em lote' : 'Deletar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCollectionManager;
